import { useState } from 'react';
import { Encounter, QuestionnaireResponse, Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { alert } from '@/services/alert';
import { useTimelineContext } from './useTimelineContext';
import { createReference, getReferenceString } from '@medplum/core';
import { collectQuestionnaireResponseUpdateFieldErrors } from '../services/questionnaireResponseUpdateErrors';
import { trimQuestionnaireResponseForSave } from '../services/trimQuestionnaireResponseForSave';
import { persistEncounterQuestionnaireResponses } from '../services/persistEncounterQuestionnaireResponses';

export function useSaveEncounterDraft(
  pendingResponses: Record<string, QuestionnaireResponse>,
  setPendingResponses: React.Dispatch<React.SetStateAction<Record<string, QuestionnaireResponse>>>,
  onSaveDraft: () => void,
  encounter: Encounter | undefined,
  handleNavigateToItem: ((itemReference: string) => void) | undefined,
  tasks: Task[],
  pendingResponsesRef?: React.MutableRefObject<Record<string, QuestionnaireResponse>>
) {
  const medplum = useMedplum();
  const { timelineContext } = useTimelineContext();
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  /** Resolves to false when the save failed (an error has already been shown). */
  const handleSaveDraft = async (options?: {
    closeAfterSave?: boolean;
    silent?: boolean;
    responsesOverride?: Record<string, QuestionnaireResponse>;
    closeOnly?: boolean;
  }): Promise<boolean> => {
    if (options?.closeOnly) {
      onSaveDraft();
      timelineContext.refresh();
      if (encounter && handleNavigateToItem) {
        const encounterReference = createReference(encounter);
        const referenceString = getReferenceString(encounterReference);
        if (referenceString) {
          handleNavigateToItem(referenceString);
        }
      }
      return true;
    }

    const responsesToSave = options?.responsesOverride ?? pendingResponsesRef?.current ?? pendingResponses;
    if (!Object.keys(responsesToSave).length) {
      return true;
    }

    const snapshotBeforeSave: Record<string, QuestionnaireResponse> = { ...responsesToSave };

    const shouldCloseAfterSave = options?.closeAfterSave !== false;
    const isSilent = options?.silent === true;

    setIsSavingDraft(true);
    try {
      const entries = Object.entries(responsesToSave);
      const tasksById = new Map<string, Task>();
      for (const task of tasks) {
        if (task.id) {
          tasksById.set(task.id, task);
        }
      }

      const pendingResponseList = entries.map(([, response]) => trimQuestionnaireResponseForSave(response));
      const results = await persistEncounterQuestionnaireResponses(
        medplum,
        entries.map(([taskId, response]) => ({ taskId, response })),
        tasksById
      );

      const hasRejected = results.some((r) => r.status === 'rejected');
      const fieldErrors = collectQuestionnaireResponseUpdateFieldErrors(pendingResponseList, results);

      if (hasRejected) {
        if (fieldErrors.length) {
          alert.error({
            title: "Couldn't save draft",
            message: `Some fields could not be processed:\n- ${fieldErrors.join('\n- ')}`,
          });
        } else {
          const firstReason = results.find((r) => r.status === 'rejected')?.reason;
          alert.error({
            title: "Couldn't save draft",
            message:
              firstReason instanceof Error ? firstReason.message : 'Unknown error updating questionnaire response',
          });
        }
        return false;
      }

      setPendingResponses(() => {
        const latest = pendingResponsesRef?.current ?? {};
        const next: Record<string, QuestionnaireResponse> = { ...latest };
        for (const taskId of Object.keys(snapshotBeforeSave)) {
          if (next[taskId] === snapshotBeforeSave[taskId]) {
            delete next[taskId];
          }
        }
        if (pendingResponsesRef) {
          pendingResponsesRef.current = next;
        }
        return next;
      });

      if (!isSilent) {
        alert.success({
          title: 'Draft saved',
          message: 'Encounter draft saved successfully.',
        });
      }
      if (shouldCloseAfterSave) {
        onSaveDraft();
      }
      const skipTimelineRefresh = isSilent && !shouldCloseAfterSave;
      if (!skipTimelineRefresh) {
        timelineContext.refresh();
      }

      if (shouldCloseAfterSave && encounter && handleNavigateToItem) {
        const encounterReference = createReference(encounter);
        const referenceString = getReferenceString(encounterReference);
        if (referenceString) {
          handleNavigateToItem(referenceString);
        }
      }
      return true;
    } catch (err) {
      alert.error({
        title: "Couldn't save draft",
        message: `Couldn't save questionnaire response: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
      return false;
    } finally {
      setIsSavingDraft(false);
    }
  };

  return {
    handleSaveDraft,
    isSavingDraft,
  };
}
